import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, TextInput, Textarea, FileInput, Label, Card, Modal } from "flowbite-react";
import { API_BASE_URL } from "../../config";
import AgregarTarjeta from "./AgregarTarjeta"; // Importar el componente para agregar tarjeta

const CrearOferta = () => {
  const [searchParams] = useSearchParams();
  const cliente_id = searchParams.get("cliente_id");

  const [form, setForm] = useState({
    titulo: "",
    descripcion: "",
    descuento: "",
    precio: "",
    codigo: "",
    fecha_expiracion: "",
    cantidad_disponible: "",
    imagen: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [tarjetaRegistrada, setTarjetaRegistrada] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    verificarTarjeta();
  }, []);

  const verificarTarjeta = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clientes/${cliente_id}/verificar_tarjeta`);
      const data = await response.json();
      if (data.message) {
        setTarjetaRegistrada(true);
      } else {
        setShowModal(true);
      }
    } catch (err) {
      console.error("Error verificando tarjeta:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setForm({ ...form, imagen: file });

    if (file) {
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    if (!tarjetaRegistrada) {
      setError("❌ Debes registrar una tarjeta en Stripe antes de crear promociones.");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("cliente_id", cliente_id);
    formData.append("titulo", form.titulo || "");
    formData.append("descripcion", form.descripcion || "");
    formData.append("descuento", form.descuento || "");
    formData.append("precio", form.precio || "");
    formData.append("codigo", form.codigo || "");
    formData.append("fecha_expiracion", form.fecha_expiracion || "");
    formData.append("cantidad_disponible", form.cantidad_disponible || "");
    if (form.imagen) {
      formData.append("imagen", form.imagen);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/cupones`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Error al crear la oferta");
      }

      setMessage("✅ Oferta creada con éxito");

      if (data.imagen_url) {
        setPreview(data.imagen_url);
      }

      setForm({
        titulo: "",
        descripcion: "",
        descuento: "",
        precio: "",
        codigo: "",
        fecha_expiracion: "",
        cantidad_disponible: "",
        imagen: null,
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center py-6 bg-gray-100">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Crear Oferta</h1>

      <Card className="w-full max-w-lg p-6">
        {!tarjetaRegistrada && (
          <Modal show={showModal} onClose={() => setShowModal(false)}>
            <Modal.Header>Registro de Tarjeta</Modal.Header>
            <Modal.Body>
              <p className="text-gray-700 mb-3">
                ⚠️ Para crear ofertas, primero debes registrar una tarjeta en Stripe.
              </p>
              <AgregarTarjeta clienteId={cliente_id} onTarjetaRegistrada={() => {
                setTarjetaRegistrada(true);
                setShowModal(false);
              }} />
            </Modal.Body>
          </Modal>
        )}

        <p className="text-gray-600 mb-4">
          📌 <strong>Nota:</strong> Por cada cupón vendido, SalyCome cobrará automáticamente un fee de $5 en Stripe.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="titulo">Título</Label>
            <TextInput name="titulo" id="titulo" placeholder="Título de la oferta" onChange={handleChange} required />
          </div>

          <div>
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea name="descripcion" id="descripcion" placeholder="Descripción de la oferta" onChange={handleChange} required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="descuento">Descuento (%)</Label>
              <TextInput type="number" name="descuento" id="descuento" placeholder="%" onChange={handleChange} required />
            </div>
            <div>
              <Label htmlFor="precio">Precio ($)</Label>
              <TextInput type="number" name="precio" id="precio" placeholder="Precio sin descuento" onChange={handleChange} required />
            </div>
          </div>

          <div>
            <Label htmlFor="cantidad_disponible">Cantidad Disponible</Label>
            <TextInput type="number" name="cantidad_disponible" id="cantidad_disponible" placeholder="Número de cupones disponibles" onChange={handleChange} required />
          </div>

          <div>
            <Label htmlFor="codigo">Código</Label>
            <TextInput name="codigo" id="codigo" placeholder="Código único de la oferta" onChange={handleChange} required />
          </div>

          <div>
            <Label htmlFor="fecha_expiracion">Fecha de Expiración</Label>
            <TextInput type="date" name="fecha_expiracion" id="fecha_expiracion" onChange={handleChange} required />
          </div>

          <div>
            <Label htmlFor="imagen">Imagen de la Oferta</Label>
            <FileInput name="imagen" id="imagen" accept="image/*" onChange={handleFileChange} required />
          </div>

          {preview && (
            <div className="flex justify-center">
              <img src={preview} alt="Vista previa" className="mt-4 w-40 h-40 object-cover border border-gray-300" />
            </div>
          )}

          {loading && <p className="text-blue-500">Subiendo oferta...</p>}
          {error && <p className="text-red-500">{error}</p>}
          {message && <p className="text-green-500">{message}</p>}

          <Button type="submit" className="w-full" disabled={loading || !tarjetaRegistrada}>
            {loading ? "Creando..." : "Crear Oferta"}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default CrearOferta;
